'use client';

import type { ReactNode } from 'react';
import { useId, useState } from 'react';
import { useConfig } from '@packages/shared/src/hooks/useConfig/useConfig';
import { ServiceLayer } from '@packages/modules/src/ServiceLayer/ServiceLayer.client';
import { useIntl } from 'react-intl';
import { useController, useFormContext } from 'react-hook-form';
import { Checkbox } from '@packages/shared/src/ui/Checkbox/Checkbox';
import { Label } from '@packages/shared/src/ui/Label/Label';
import { Dialog } from '@packages/shared/src/ui/Dialog/Dialog';
import { DialogContent } from '@packages/shared/src/ui/Dialog/DialogContent/DialogContent';
import { DialogHeader } from '@packages/modules/src/Dialog/DialogHeader/DialogHeader.client';
import { customerReviewTermsMessages } from './CustomerReviewTerms.messages';
import type { CreateReviewFormSchema } from '../AddReviewFormValidation/AddReviewFormValidation';
import { useCustomerReviewsTracking } from '../../Tracking/useCustomerReviewsTracking/useCustomerReviewsTracking';

/**
 * Shows customer review terms
 */
export const CustomerReviewTerms = () => {
  const { formatMessage } = useIntl();
  const {
    customerRatings: { termsId },
  } = useConfig();
  const { trackCustomerReviewTermsDisplayOverlay } = useCustomerReviewsTracking();
  const {
    formState: { errors },
    control,
  } = useFormContext<CreateReviewFormSchema>();
  const {
    field: { onChange, value },
  } = useController({
    control,
    name: 'accept',
  });
  const id = useId();
  const [dialogHeadline, setdialogHeadline] = useState<string | null>(null);

  const handleDialogOpen = () => {
    const newDialogHeadline = formatMessage(customerReviewTermsMessages.dialogHeadline);
    setdialogHeadline(newDialogHeadline);
    trackCustomerReviewTermsDisplayOverlay(newDialogHeadline);
  };

  const handleDialogClose = () => {
    setdialogHeadline(null);
  };

  return (
    <div className="-ml-1 [&_div]:justify-start">
      <Checkbox
        id={id}
        checked={!!value}
        onChange={() => onChange(!value)}
        {...(errors.accept && {
          error: true,
          errorMessage: formatMessage(customerReviewTermsMessages.error),
        })}
      >
        <Label htmlFor={id}>
          {formatMessage(customerReviewTermsMessages.label, {
            link: (txt: ReactNode) => (
              <span
                className="underline"
                role="button"
                onClick={handleDialogOpen}
                onKeyDown={(event) => {
                  if (event.key === 'Enter') {
                    handleDialogOpen();
                  }
                }}
                aria-haspopup="dialog"
                tabIndex={0}
              >
                {txt}
              </span>
            ),
          })}
        </Label>
      </Checkbox>
      {dialogHeadline && (
        <Dialog isOpen={dialogHeadline !== null} title={dialogHeadline} onClose={handleDialogClose}>
          <DialogHeader onClose={handleDialogClose}>{dialogHeadline}</DialogHeader>
          <DialogContent>
            <ServiceLayer serviceLayerId={termsId} />
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
};
